import { cva, type VariantProps } from 'class-variance-authority'
import { Button as AriaButton, type ButtonProps as AriaButtonProps } from 'react-aria-components'
import { cn } from '../lib/cn'

/**
 * Variant styles for {@link Button}.
 *
 * Every color, radius, and type-scale class resolves to a design token registered
 * via `@theme` (see `styles/theme.css` in packages/ui-primitives/src/styles/theme.css:12-95),
 * so no hardcoded color values appear here.
 *
 * All colors come from @atlas/tokens, sourced from the ATLAS design tokens.
 *
 * @remarks
 * - Primary: the brand primary color
 * - Secondary: the brand secondary color
 * - Danger: Error red from semantic tokens
 * - Success: Success green from semantic tokens
 * - Muted: Surface/border colors for subdued filled buttons
 * - Outline: token border on a transparent background
 * - Ghost: transparent with a surface hover only
 * - Dashed: a dashed token border on a transparent background, for the low-emphasis
 *   "add one of these" affordance that sits where the new item will appear
 *
 * @example
 * ```tsx
 * const classes = buttonVariants({ variant: 'primary', size: 'md' })
 * // Returns: "inline-flex items-center ... bg-ds-primary text-ds-primary-foreground ..."
 * ```
 */
export const buttonVariants = cva(
	'inline-flex items-center justify-center gap-2 font-normal transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ds-ring cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 disabled:pointer-events-none rounded-ds-md',
	{
		variants: {
			variant: {
				primary: 'bg-ds-primary text-ds-primary-foreground hover:bg-ds-primary-hover',
				secondary: 'bg-ds-secondary text-ds-text hover:bg-ds-surface-elevated border border-ds-border',
				danger: 'bg-ds-danger text-ds-danger-foreground hover:opacity-90',
				success: 'bg-ds-success text-ds-success-foreground hover:opacity-90',
				muted: 'bg-ds-surface text-ds-text-muted hover:bg-ds-surface-elevated border border-ds-border',
				outline: 'border border-ds-border bg-transparent text-ds-text hover:bg-ds-surface',
				ghost: 'bg-transparent text-ds-text hover:bg-ds-surface',
				dashed:
					'border border-dashed border-ds-border bg-transparent text-ds-text-muted hover:border-ds-text-subtle hover:text-ds-text',
			},
			size: {
				sm: 'text-ds-sm px-3 py-1.5 h-8',
				md: 'text-ds-base px-4 py-2 h-10',
				lg: 'text-ds-lg px-6 py-3 h-12',
			},
		},
		defaultVariants: { variant: 'primary', size: 'md' },
	},
)

/**
 * Props for the {@link Button} component.
 *
 * Extends React Aria's ButtonProps with CVA variant props for styling.
 *
 * @property variant - Visual style: 'primary' | 'secondary' | 'danger' | 'success' | 'muted' | 'outline' | 'ghost' | 'dashed'
 * @property size - Button size: 'sm' (h-8) | 'md' (h-10, default) | 'lg' (h-12)
 * @property className - Optional additional Tailwind classes
 * @property children - Button label/content
 * @property onPress - Click handler (React Aria pattern)
 * @property isDisabled - Disabled state (React Aria pattern)
 *
 * @example
 * ```tsx
 * <Button variant="primary" size="md" onPress={() => console.log('Clicked')}>
 *   Save Changes
 * </Button>
 * ```
 */
export interface ButtonProps extends Omit<AriaButtonProps, 'className'>, VariantProps<typeof buttonVariants> {
	className?: string
}

/**
 * Accessible button primitive using React Aria Components.
 *
 * @remarks
 * - **Accessibility:** Keyboard navigation, focus management, and screen reader support
 *   come from React Aria's `<Button>` primitive.
 * - **Styling:** All colors and spacing resolve to design tokens from `@atlas/tokens`
 *   (brand). No hardcoded values.
 * - **Variants:** Primary (brand primary), secondary, danger, success, muted.
 * - **Sizes:** Small (h-8), medium (h-10, default), large (h-12).
 *
 * @example Basic usage
 * ```tsx
 * import { Button } from '@atlas/ui-primitives'
 *
 * function MyForm() {
 *   return (
 *     <form>
 *       <Button variant="primary" onPress={handleSubmit}>
 *         Submit
 *       </Button>
 *       <Button variant="muted" onPress={handleCancel}>
 *         Cancel
 *       </Button>
 *     </form>
 *   )
 * }
 * ```
 *
 * @example With disabled state
 * ```tsx
 * <Button variant="primary" isDisabled={!isFormValid}>
 *   Save
 * </Button>
 * ```
 *
 * @see {@link buttonVariants} for CVA variant configuration
 * @see packages/ui-primitives/src/button/button.stories.tsx for visual examples
 */
export function Button({ variant, size, className, ...props }: ButtonProps) {
	return <AriaButton {...props} className={cn(buttonVariants({ variant, size }), className)} />
}
